import { textSm } from '@insify/design-system/typography';
import { motion } from 'motion/react';

import { HTML_TAGS } from '../../utils/markdown';

import { TypewriterItem } from './TypewriterItem';

import type { ForwardRefComponent, HTMLMotionProps, Variants } from 'motion/react';
import type { ClassAttributes, ElementType, HTMLAttributes, RefObject } from 'react';
import type { Components, ExtraProps } from 'react-markdown';

export function generateMarkdownComponents({
  characterVariants,
  onCharacterAnimationComplete,
  delay,
}: {
  characterVariants: Variants;
  onCharacterAnimationComplete?: (letterRef: RefObject<HTMLSpanElement | null>) => void;
  delay: number;
}): Components {
  const components: Components = {};

  const sentenceVariants = {
    hidden: characterVariants.hidden,
    visible: {
      ...characterVariants.visible,
      opacity: 1,
      transition: { staggerChildren: delay / 1000 },
    },
  };
  HTML_TAGS.forEach((tag) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const MotionComponent: ForwardRefComponent<HTMLElement, HTMLMotionProps<any>> = (motion as any)[tag];
      if (MotionComponent) {
        const fn: ElementType<ClassAttributes<HTMLElement> & HTMLAttributes<HTMLElement> & ExtraProps> = (props) => {
          const { children, id, className } = props;
          const { node: _node, ref: _ref, ...componentProps } = props;
          switch (tag) {
            case 'table':
            case 'input':
            case 'hr':
            case 'img':
              return (
                <MotionComponent
                  {...componentProps}
                  key={`${tag}-${id}`}
                  className={textSm}
                  variants={className ? undefined : sentenceVariants}
                  onAnimationComplete={onCharacterAnimationComplete}
                >
                  {children}
                </MotionComponent>
              );
            case 'tr':
            case 'th':
            case 'td':
            case 'pre':
            case 'code':
              return <MotionComponent {...componentProps}>{children}</MotionComponent>;
            // case 'math':
            //   console.log('TypewriterItem for p tag is deprecated, use span instead', children);
            //   return (
            //     <TypewriterItem
            //       key={id}
            //       characterVariants={characterVariants}
            //       onCharacterAnimationComplete={onCharacterAnimationComplete}
            //       parent={(children) => {
            //         if (Array.isArray(children)) {
            //           children.push(
            //             <motion.span key={`span-${id}-newline`} style={{ display: 'block', height: 0, width: 0 }} />,
            //           );
            //           return children;
            //         }
            //         return children;
            //       }}
            //     >
            //       {children}
            //     </TypewriterItem>
            //   );
            case 'span':
            case 'p':
              return (
                <TypewriterItem
                  key={id}
                  characterVariants={characterVariants}
                  onCharacterAnimationComplete={onCharacterAnimationComplete}
                  className={textSm}
                  parent={(children) => {
                    if (Array.isArray(children)) {
                      return (
                        <MotionComponent className={textSm} {...componentProps} key={`${tag}-${id}`}>
                          {children}
                        </MotionComponent>
                      );
                    }
                    return children;
                  }}
                >
                  {children}
                </TypewriterItem>
              );
            case 'ul':
            case 'ol':
            case 'li':
              return (
                <TypewriterItem
                  key={id}
                  characterVariants={characterVariants}
                  onCharacterAnimationComplete={onCharacterAnimationComplete}
                  className={textSm}
                  parent={(children) => {
                    return (
                      <MotionComponent
                        {...componentProps}
                        key={`${tag}-${id}`}
                        variants={className ? undefined : sentenceVariants}
                      >
                        {children}
                      </MotionComponent>
                    );
                  }}
                >
                  {children}
                </TypewriterItem>
              );
            default:
              return (
                <TypewriterItem
                  key={id}
                  characterVariants={characterVariants}
                  onCharacterAnimationComplete={onCharacterAnimationComplete}
                  parent={(children, isString) => {
                    return (
                      <MotionComponent
                        {...componentProps}
                        key={`${tag}-${id}`}
                        variants={isString || className ? undefined : sentenceVariants}
                      >
                        {children}
                      </MotionComponent>
                    );
                  }}
                >
                  {children}
                </TypewriterItem>
              );
          }
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (components as any)[tag] = fn;
      }
    } catch (_) {
      // If the tag is not a valid motion component, we skip it
      console.warn(`Skipping invalid motion component for tag: ${tag}`);
    }
  });
  return components;
}
